import React, {useMemo, useState} from "react";
import {Collapse} from "react-bootstrap";
import {Link, usePage} from "@inertiajs/react";
import {PiList, PiX} from "react-icons/pi";
import NutzungFilter from "./NutzungFilter.jsx";
import {BEREICHE} from "./format.js";

/**
 * Gemeinsamer Rahmen ALLER Seiten der Nutzungsauswertung (Spezifikation
 * Abschnitt 1 + 5, Entwurf "nutzung-konzept-neu"): seitliches, gruppiertes
 * Untermenü, eine Brotkrumen-Zeile und darunter die bestehende
 * Zeitraum-Filterleiste (NutzungFilter) - unverändert wiederverwendet,
 * damit Voreinstellung/Von/Bis/Mitarbeiter/Bereich überall gleich
 * funktionieren.
 *
 * Auch die zentrale "nutzung-link"-Klasse für Links.jsx (MitarbeiterLink,
 * BereichLink, SeiteLink) wird hier EINMAL definiert, weil dieser Rahmen
 * auf jeder Nutzungs-Seite sitzt - jede Detailseite, die Links.jsx benutzt,
 * bekommt die Optik automatisch mit, ohne dass jede Stelle eigenes CSS
 * mitbringen müsste.
 */
const STYLE = `
.nutzung-link{color:var(--bs-primary);text-decoration:underline;text-decoration-style:dotted;text-decoration-color:rgba(var(--bs-primary-rgb),.55);text-underline-offset:2px}
.nutzung-link:hover{text-decoration-style:solid}
.nutzung-menu-eintrag{color:#3d4757}
.nutzung-menu-eintrag:hover{background:rgba(0,0,0,.045);color:#1f2430}
.nutzung-menu-eintrag-aktiv,.nutzung-menu-eintrag-aktiv:hover{background:rgba(var(--bs-primary-rgb),.14);color:#1f2430;font-weight:600;box-shadow:inset 3px 0 0 var(--bs-primary)}
.nutzung-gruppe-titel{font-size:.68rem;text-transform:uppercase;letter-spacing:.05em}
@keyframes nutzungPuls{50%{opacity:.35}}
.nutzung-puls{animation:nutzungPuls 1.8s ease-in-out infinite}
`;

/**
 * Menüstruktur exakt nach Spezifikation Abschnitt 1. Die Bereichs-Gruppe
 * wird um die feste Bereichsliste aus format.js ergänzt (dieselbe Liste,
 * die auch der Bereichsfilter benutzt) - so führt "Bereiche" im Menü sofort
 * zu jedem einzelnen Bereich, statt nur zur Übersicht, ohne dafür einen
 * eigenen Endpunkt zu brauchen.
 */
const GRUNDGRUPPEN = [
    {
        gruppe: "Jetzt",
        eintraege: [
            {label: "Wer ist online", href: "/nutzung"},
            {label: "Aktuelle Wege", href: "/nutzung/wege-jetzt"},
            {label: "Ereignisstrom", href: "/nutzung/strom"},
        ],
    },
    {
        gruppe: "Mitarbeiter",
        eintraege: [
            {label: "Alle Mitarbeiter", href: "/nutzung/mitarbeiter"},
            {label: "Vergleich", href: "/nutzung/vergleich"},
            {label: "Anwesenheitszeiten", href: "/nutzung/anwesenheit"},
        ],
    },
    {
        gruppe: "Bereiche",
        eintraege: [
            {label: "Übersicht", href: "/nutzung/bereiche"},
            ...BEREICHE.map((eintrag) => ({label: eintrag.label, href: `/nutzung/bereiche/${eintrag.value}`})),
        ],
    },
    {
        gruppe: "Seiten",
        eintraege: [
            {label: "Meistgenutzt", href: "/nutzung/seiten"},
            {label: "Nie benutzt", href: "/nutzung/seiten/ungenutzt"},
            {label: "Wege durchs System", href: "/nutzung/wege"},
        ],
    },
    {
        gruppe: "Änderungen",
        eintraege: [{label: "Alle Änderungen", href: "/nutzung/aenderungen"}],
    },
    {
        gruppe: "Verwaltung",
        eintraege: [
            {label: "Erfassung + Speicher", href: "/nutzung/einstellungen"},
            {label: "Zugriffsprotokoll", href: "/nutzung/zugriffe"},
            {label: "Rohdaten", href: "/nutzung/ereignisse"},
        ],
    },
];

/**
 * Fügt das gerade betrachtete Einzelprofil als eigenen Menüpunkt in die
 * Mitarbeiter-Gruppe ein - ohne festen Namen im Menü ergäbe "Einzelprofil"
 * kein anklickbares Ziel. Wird nur gezeigt, während man sich tatsächlich
 * auf einem Profil befindet (siehe Profil.jsx, Prop "aktuellesProfil").
 */
function baueGruppen(aktuellesProfil) {
    if (!aktuellesProfil?.id) {
        return GRUNDGRUPPEN;
    }
    return GRUNDGRUPPEN.map((gruppe) => {
        if (gruppe.gruppe !== "Mitarbeiter") {
            return gruppe;
        }
        const eintrag = {
            label: aktuellesProfil.name || `Nutzer ${aktuellesProfil.id}`,
            href: `/nutzung/mitarbeiter/${aktuellesProfil.id}`,
        };
        return {...gruppe, eintraege: [gruppe.eintraege[0], eintrag, ...gruppe.eintraege.slice(1)]};
    });
}

export default function NutzungLayout({
    gruppe,
    titel,
    breadcrumbLabel,
    nutzungFilters,
    showMitarbeiter = true,
    showBereich = true,
    aktuellesProfil = null,
    children,
}) {
    const {url} = usePage();
    const pfad = url.split("?")[0];
    const [menuOffen, setMenuOffen] = useState(false);

    const gruppen = useMemo(() => baueGruppen(aktuellesProfil), [aktuellesProfil?.id, aktuellesProfil?.name]);
    const istAktiv = (href) => pfad === href;

    const navInhalt = (
        <nav aria-label="Bereiche der Nutzungsauswertung" className="d-flex flex-column">
            {gruppen.map((abschnitt) => (
                <div key={abschnitt.gruppe} className="mb-3">
                    <div className="nutzung-gruppe-titel text-muted fw-semibold px-2 mb-1">{abschnitt.gruppe}</div>
                    <div className="d-flex flex-column gap-1">
                        {abschnitt.eintraege.map((eintrag) => (
                            <Link
                                key={eintrag.href}
                                href={eintrag.href}
                                aria-current={istAktiv(eintrag.href) ? "page" : undefined}
                                className={`nutzung-menu-eintrag rounded px-2 py-1 small text-decoration-none ${
                                    istAktiv(eintrag.href) ? "nutzung-menu-eintrag-aktiv" : ""
                                }`}
                            >
                                {eintrag.label}
                            </Link>
                        ))}
                    </div>
                </div>
            ))}
        </nav>
    );

    return (
        <>
            <style>{STYLE}</style>
            <div className="d-flex flex-column flex-lg-row gap-4 align-items-start">
                {/* Schmale Geräte: Menü klappt oben zusammen statt seitlich zu stehen. */}
                <div className="d-lg-none w-100">
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2 mb-2"
                        onClick={() => setMenuOffen((offen) => !offen)}
                        aria-expanded={menuOffen}
                    >
                        {menuOffen ? <PiX aria-hidden="true" /> : <PiList aria-hidden="true" />}
                        Menü der Nutzungsauswertung
                    </button>
                    <Collapse in={menuOffen}>
                        <div className="border rounded p-2 mb-2 bg-body">{navInhalt}</div>
                    </Collapse>
                </div>

                <aside className="d-none d-lg-block flex-shrink-0" style={{width: 224}}>
                    {navInhalt}
                </aside>

                <div className="flex-grow-1" style={{minWidth: 0}}>
                    <nav aria-label="Sie befinden sich hier" className="small text-muted mb-2">
                        {gruppe} <span aria-hidden="true">›</span>{" "}
                        <span className="text-body fw-semibold">{breadcrumbLabel || titel}</span>
                    </nav>

                    <NutzungFilter
                        title={titel}
                        nutzungFilters={nutzungFilters}
                        showMitarbeiter={showMitarbeiter}
                        showBereich={showBereich}
                    >
                        {children}
                    </NutzungFilter>
                </div>
            </div>
        </>
    );
}
